import React, { useState } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import { DrawerLayoutWrapper } from 'common';
import ProgressBar from 'feature/dispatching/components/ProgressBar';

const notEmpty = R.compose(R.not, R.isEmpty);

const TrailerInformation = ({ onClose, next, trailerState, setTrailerState }) => {
  const { vaporTightnessCode, hasMexicanTrailerNumber, mexicanTrailerNumber } = trailerState;

  const [validationState, setValidationState] = useState({
    mexicanTrailerNumber: '',
    vaporTightnessCode: 'Must only be numbers',
  });

  const isInvalid = [
    R.isEmpty(vaporTightnessCode),
    notEmpty(validationState.vaporTightnessCode),
    R.and(hasMexicanTrailerNumber, R.isEmpty(mexicanTrailerNumber)),
  ].includes(true);

  const handleHasMexicanTrailerNumberChange = R.compose(
    setTrailerState,
    checked =>
      R.mergeLeft({
        hasMexicanTrailerNumber: checked,
        mexicanTrailerNumber: '',
      }),
    R.path(['target', 'checked'])
  );

  const validateVaporTightnessCode = R.compose(
    setValidationState,
    R.assoc('vaporTightnessCode'),
    R.ifElse(R.isEmpty, R.always('Trailer ID required'), R.always(''))
  );

  const restrictCharacters = value => value.replace(/[^0-9]/gi, '');

  const handleVaporTightnessCodeChange = R.compose(
    setTrailerState,
    R.assoc('vaporTightnessCode'),
    R.tap(validateVaporTightnessCode),
    restrictCharacters,
    R.path(['target', 'value'])
  );

  const validateMexicanTrailerNumber = R.compose(
    setValidationState,
    R.assoc('mexicanTrailerNumber'),
    R.ifElse(R.isEmpty, R.always('Mexican Trailer Number required'), R.always(''))
  );

  const handleMexicanTrailerNumberChange = R.compose(
    setTrailerState,
    R.assoc('mexicanTrailerNumber'),
    R.tap(validateMexicanTrailerNumber),
    R.path(['target', 'value'])
  );

  const buttons = (
    <Box sx={{ display: 'flex', justifyContent: 'space-around', width: 1 }}>
      <Button disabled={isInvalid} onClick={next} sx={{ width: 150 }}>
        Continue
      </Button>
    </Box>
  );

  return (
    <DrawerLayoutWrapper
      buttons={buttons}
      headerButtonText="CLOSE"
      onHeaderButtonClick={onClose}
      title="New Trailer"
    >
      <ProgressBar
        label="Trailer (1 of 5)"
        sx={{
          '&.MuiLinearProgress-colorPrimary > .MuiLinearProgress-bar': {
            borderRadius: '10px',
          },
        }}
        value={20}
      />
      <Stack spacing={2} sx={{ p: 3 }}>
        <Stack direction="row" sx={{ alignItems: 'center', mb: 2.25 }}>
          <FormControlLabel
            control={
              <Switch
                checked={hasMexicanTrailerNumber}
                onChange={handleHasMexicanTrailerNumberChange}
              />
            }
            label="MX Customs Trailer Number"
          />
          <Tooltip
            placement="top"
            slotProps={{
              popper: { sx: { maxWidth: 200, p: 1, textAlign: 'center' } },
            }}
            title="Optional trailer number used when crossing the Mexican/US border."
          >
            <InfoRoundedIcon sx={{ '&:hover': { cursor: 'pointer' }, color: 'graphic.main' }} />
          </Tooltip>
        </Stack>

        <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center', mb: 2.25 }}>
          <Typography variant="body1">Trailer Information</Typography>
          <Tooltip
            placement="top"
            slotProps={{
              popper: { sx: { maxWidth: 200, p: 1, textAlign: 'center' } },
            }}
            title="TrailerID is the Vapor Tightness Certificate (VTC) number."
          >
            <InfoRoundedIcon sx={{ '&:hover': { cursor: 'pointer' }, color: 'graphic.main' }} />
          </Tooltip>
        </Stack>

        <TextField
          error={notEmpty(validationState.vaporTightnessCode)}
          fullWidth
          helperText={validationState.vaporTightnessCode}
          label="Trailer ID"
          onChange={handleVaporTightnessCodeChange}
          slotProps={{
            htmlInput: { maxLength: 12 },
          }}
          value={vaporTightnessCode}
        />
        {hasMexicanTrailerNumber && (
          <TextField
            error={notEmpty(validationState.mexicanTrailerNumber)}
            fullWidth
            helperText={validationState.mexicanTrailerNumber}
            label="MX Customs Trailer Number"
            onChange={handleMexicanTrailerNumberChange}
            value={mexicanTrailerNumber}
          />
        )}
      </Stack>
    </DrawerLayoutWrapper>
  );
};

TrailerInformation.propTypes = {
  next: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  setTrailerState: PropTypes.func.isRequired,
  trailerState: PropTypes.shape({
    hasMexicanTrailerNumber: PropTypes.bool,
    mexicanTrailerNumber: PropTypes.string,
    vaporTightnessCode: PropTypes.string,
  }).isRequired,
};

export default TrailerInformation;
